import type { DashboardProject } from "../types.js";
import {
  createProjectRecord,
  getProjectsWorkspaceSnapshot,
  getSeededProjectRecords,
  PROJECTS_WORKSPACE_TARGET,
} from "../projects/api/projectsApi";
import {
  buildProjectsSummary,
  filterDashboardProjects,
  getProjectsDisplayStatus,
  mapProjectRecordToViewModel,
  mapProjectRecordsToViewModels,
  mapWorkspaceSnapshotToViewModel,
} from "../projects/mappers/projectsMappers";
import type {
  DashboardProjectCreateRequestDto,
  ProjectsSummary,
  ProjectsTabId,
  ProjectsWorkspaceSnapshotViewModel as ProjectsWorkspaceSnapshot,
} from "../projects/types/projects.api";

export { PROJECTS_TAB_OPTIONS } from "../projects/types/projects.api";
export type { ProjectsSummary, ProjectsTabId, ProjectsWorkspaceSnapshot };

export { PROJECTS_WORKSPACE_TARGET };

export interface ProjectsSectionViewModel {
  projects: DashboardProject[];
  summary: ProjectsSummary;
  workspace: ProjectsWorkspaceSnapshot;
}

export interface CreateDashboardProjectInput
  extends DashboardProjectCreateRequestDto {}

export { buildProjectsSummary, filterDashboardProjects, getProjectsDisplayStatus };

export function createDashboardProject(
  input: CreateDashboardProjectInput,
): DashboardProject {
  return mapProjectRecordToViewModel(createProjectRecord(input));
}

const WORKSPACE_BACKEND_STATES = new Set(["IN_PROGRESS", "ACTIVE", "UPDATED"]);

function hasValidStatusContract(snapshot: ProjectsWorkspaceSnapshot): boolean {
  return (
    typeof snapshot.companyId === "string" &&
    snapshot.companyId.trim().length > 0 &&
    typeof snapshot.productId === "string" &&
    snapshot.productId.trim().length > 0 &&
    typeof snapshot.backendState === "string" &&
    WORKSPACE_BACKEND_STATES.has(snapshot.backendState)
  );
}

function hasValidDraftContract(snapshot: ProjectsWorkspaceSnapshot): boolean {
  const hasValidDraftRevision =
    snapshot.draftRevision === null ||
    (typeof snapshot.draftRevision === "number" &&
      Number.isFinite(snapshot.draftRevision));
  const hasValidDraftStepCount =
    snapshot.draftStepCount === null ||
    (typeof snapshot.draftStepCount === "number" &&
      Number.isFinite(snapshot.draftStepCount));

  return hasValidDraftRevision && hasValidDraftStepCount;
}

function toWorkspaceContractError(
  snapshot: ProjectsWorkspaceSnapshot,
  error: string,
): ProjectsWorkspaceSnapshot {
  return {
    ...snapshot,
    status: "error",
    error,
    backendState: null,
    draftRevision: null,
    draftStepCount: null,
  };
}

export async function loadProjectsWorkspaceSnapshot(): Promise<ProjectsWorkspaceSnapshot> {
  const snapshot = mapWorkspaceSnapshotToViewModel(await getProjectsWorkspaceSnapshot());
  if (snapshot.status !== "ready") {
    return snapshot;
  }

  if (!hasValidStatusContract(snapshot)) {
    return toWorkspaceContractError(
      snapshot,
      "Invalid project status payload from workspace snapshot.",
    );
  }
  if (!hasValidDraftContract(snapshot)) {
    return toWorkspaceContractError(
      snapshot,
      "Invalid project draft payload from workspace snapshot.",
    );
  }

  return snapshot;
}

export function getProjectsSectionViewModel(
  projects: DashboardProject[] = mapProjectRecordsToViewModels(getSeededProjectRecords()),
): ProjectsSectionViewModel {
  return {
    projects,
    summary: buildProjectsSummary(projects),
    workspace: mapWorkspaceSnapshotToViewModel({
      status: "loading",
      error: null,
      companyId: PROJECTS_WORKSPACE_TARGET.companyId,
      productId: PROJECTS_WORKSPACE_TARGET.productId,
      companyName: PROJECTS_WORKSPACE_TARGET.companyName,
      projectName: PROJECTS_WORKSPACE_TARGET.projectName,
      backendState: null,
      previewUrl: null,
      liveUrl: null,
      draftRevision: null,
      draftStepCount: null,
      hasLiveDeployment: false,
      updatedAtLabel: null,
    }),
  };
}
